import { Link, List, ListItem, ListItemText, Typography } from "@mui/material";
import i18nPackage from "../../i18n/i18n";
import styles from "./index.module.css";
import { AppliedLimitsKey } from "../before-quiz";
import RightClick from "../../images/right-click.svg?react";
import DisableNewTabs from "../../images/new-tabs-disabled.svg?react";
import Clipboard from "../../images/clipboard-disabled.svg?react";
import WebTraffic from "../../images/web-traffic-record.svg?react";
import Download from "../../images/download-disabled.svg?react";
import Printing from "../../images/printing.disabled.svg?react";
import CloseTabs from "../../images/close-tabs.svg?react";
import HideApplications from "../../images/hide-application.svg?react";
import LogInstalledExtensions from "../../images/log-installed-extensions.svg?react";
import PolicyIcon from "../../images/shield-magnify.svg?react";
import InstitutionIcon from "../../images/grad-hat.svg?react";
import AssessmentResourcesIcon from "../../images/permitted-urls.svg?react";
import AppliedLimitsIcon from "../../images/circle-warning.svg?react";
import { Trans } from "react-i18next";

type ReviewStepProps = {
	pdfUrl?: string;
	limits?: AppliedLimitsKey[];
	installedExtensionsCount?: number;
	allowedUrls?: string[];
};

const logExtensionsKey: AppliedLimitsKey = "logInstalledExtensions";

export default function ReviewStep({ pdfUrl, limits, installedExtensionsCount, allowedUrls }: ReviewStepProps) {
	const filteredLimits = limits?.filter((limit) => limit !== logExtensionsKey) ?? [];
	const hasLogInstalledExtensions = limits?.includes(logExtensionsKey) ?? false;
	return (
		<div className={styles.reviewContainer}>
			<div className={styles.reviewTitleWrapper}>
				<Typography className={styles.reviewTitle}>{i18nPackage.t("beforeQuiz.review.title")}</Typography>
				<Typography className={styles.reviewDescription}>{i18nPackage.t("beforeQuiz.review.description")}</Typography>
			</div>
			<PolicySection />
			{pdfUrl && <PDFSection pdfUrl={pdfUrl} />}
			{filteredLimits && filteredLimits.length > 0 && <AppliedLimitsSection limits={filteredLimits} />}
			{allowedUrls && allowedUrls.length > 0 && <AllowedUrlsSection allowedUrls={allowedUrls} />}
			{hasLogInstalledExtensions && <InstalledExtensionsSection installedExtensionsCount={installedExtensionsCount} />}
		</div>
	);
}

function PolicySection() {
	return (
		<div className={styles.reviewSectionWrapper}>
			<div className={styles.sectionTitleWrapper}>
				<PolicyIcon aria-hidden="true" />
				<Typography variant="h2">{i18nPackage.t("beforeQuiz.review.policyTitle")}</Typography>
			</div>
			<Typography className={styles.reviewSectionDescription}>
				{i18nPackage.t("beforeQuiz.review.policyDescription")}
			</Typography>
			<List className={styles.policyList}>
				<ListItem>
					<ListItemText primary={i18nPackage.t("beforeQuiz.review.policy1")} />
				</ListItem>
				<ListItem>
					<ListItemText primary={i18nPackage.t("beforeQuiz.review.policy2")} />
				</ListItem>
			</List>
		</div>
	);
}

// Css will color the icons, so do not need to define here
const AppliedLimitsIconMap: Record<AppliedLimitsKey, React.ReactNode> = {
	disableRightClick: <RightClick aria-hidden="true" />,
	disableNewTab: <DisableNewTabs aria-hidden="true" />,
	disableClipboardMode: <Clipboard aria-hidden="true" />,
	closeOpenTabs: <CloseTabs aria-hidden="true" />,
	recordWebTraffic: <WebTraffic aria-hidden="true" />,
	disableDownloadFiles: <Download aria-hidden="true" />,
	disablePrinting: <Printing aria-hidden="true" />,
	hideOtherWindows: <HideApplications aria-hidden="true" />,
	logInstalledExtensions: <LogInstalledExtensions aria-hidden="true" />,
};

function AppliedLimitsSection({ limits }: { limits: AppliedLimitsKey[] }) {
	return (
		<div className={styles.reviewSectionWrapper}>
			<div className={styles.sectionTitleWrapper}>
				<AppliedLimitsIcon aria-hidden="true" />
				<Typography variant="h2">{i18nPackage.t("beforeQuiz.review.limitedActionsTitle")}</Typography>
			</div>
			<Typography className={styles.reviewSectionDescription}>
				{i18nPackage.t("beforeQuiz.review.limitedActionsDescription")}
			</Typography>

			<div className={styles.appliedLimitsContainer}>
				{limits.map((limit) => {
					return (
						<div className={styles.appliedLimitItemRow} key={limit}>
							{AppliedLimitsIconMap[limit]}
							<Typography>{i18nPackage.t(`beforeQuiz.review.appliedLimits.${limit}`)}</Typography>
						</div>
					);
				})}
			</div>
		</div>
	);
}

function InstalledExtensionsSection({ installedExtensionsCount }: { installedExtensionsCount?: number }) {
	return (
		<div className={styles.reviewSectionWrapper}>
			<div className={styles.sectionTitleWrapper}>
				<LogInstalledExtensions color="#5925c8" width={24} height={24} />
				<Typography variant="h2">{i18nPackage.t("beforeQuiz.review.installedExtensionsTitle")}</Typography>
			</div>
			<Typography className={styles.reviewSectionDescription}>
				<Trans
					i18n={i18nPackage}
					i18nKey="beforeQuiz.review.installedExtensionsDescription"
					values={{ count: installedExtensionsCount ?? 0 }}
					components={{
						strong: <strong />,
					}}
				/>
			</Typography>
		</div>
	);
}

function AllowedUrlsSection({ allowedUrls }: { allowedUrls: string[] }) {
	return (
		<div className={styles.reviewSectionWrapper}>
			<div className={styles.sectionTitleWrapper}>
				<AssessmentResourcesIcon />
				<Typography variant="h2">{i18nPackage.t("beforeQuiz.review.allowedUrlsTitle")}</Typography>
			</div>
			<Typography className={styles.reviewSectionDescription}>
				{i18nPackage.t("beforeQuiz.review.allowedUrlsDescription")}
			</Typography>
			<List className={styles.policyList}>
				{allowedUrls.map((url) => (
					<ListItem key={url}>
						<ListItemText primary={<Typography className={styles.allowedUrlText}>{url}</Typography>} />
					</ListItem>
				))}
			</List>
		</div>
	);
}

export function PDFSection({ pdfUrl }: { pdfUrl: string }) {
	return (
		<div className={styles.reviewSectionWrapper}>
			<div className={styles.sectionTitleWrapper}>
				<InstitutionIcon />
				<Typography variant="h2" className={styles.reviewHeader}>
					{i18nPackage.t("beforeQuiz.review.institutionTitle")}
				</Typography>
			</div>
			<Typography className={styles.reviewSectionDescription}>
				<Trans
					i18n={i18nPackage}
					i18nKey="beforeQuiz.review.institutionDescription"
					components={{
						a: (
							<Link
								href={pdfUrl}
								target="_blank"
								rel="noopener noreferrer"
								aria-label={i18nPackage.t("beforeQuiz.review.policyAriaLabel")}
								sx={{
									verticalAlign: "baseline",
								}}
							/>
						),
					}}
				/>
			</Typography>
		</div>
	);
}
